import { useSyncExternalStore } from "react";

/**
 * How the CRM writes the time of day: "2:30 pm" (12h, the default) or "14:30" (24h). A company sets the default and a
 * person can choose their own; the signed-in user's effective choice is put here when they sign in or change it.
 *
 * Every place that shows a time of day goes through this file (a CI check fails the build if one doesn't), so one
 * setting changes the whole app. Wrap the options of `toLocaleTimeString` / `toLocaleString` in `withClock(...)`.
 */
export type TimeFormat = "12h" | "24h";
/** The order of a date: "dmy" is "21 Oct 2026" / 21/10/2026 (the default), "mdy" is "Oct 21, 2026" / 10/21/2026 */
export type DateFormat = "dmy" | "mdy";

let current: TimeFormat = "12h";
let currentDate: DateFormat = "dmy";
const listeners = new Set<() => void>();

export const normalizeTimeFormat = (v: unknown): TimeFormat | null => (v === "12h" || v === "24h" ? v : null);

export function getTimeFormat(): TimeFormat {
  return current;
}

/** Called when the signed-in user's effective choice is known or changes; anything that shows a time re-renders */
export function setTimeFormat(v: unknown) {
  const next = normalizeTimeFormat(v) ?? "12h";
  if (next === current) return;
  current = next;
  listeners.forEach((l) => l());
}

export const normalizeDateFormat = (v: unknown): DateFormat | null => (v === "dmy" || v === "mdy" ? v : null);

export function getDateFormat(): DateFormat {
  return currentDate;
}

/** Called when the signed-in user's effective date style is known or changes */
export function setDateFormat(v: unknown) {
  const next = normalizeDateFormat(v) ?? "dmy";
  if (next === currentDate) return;
  currentDate = next;
  listeners.forEach((l) => l());
}

/**
 * The locale that writes dates (and times of day) in the chosen order. Every date and time on screen takes its locale
 * from here (a CI check fails the build if one is hard-coded): day-first keeps the "2:30 pm" look, month-first is the US one.
 */
export function dateLocale(f: DateFormat = currentDate): string {
  return f === "mdy" ? "en-US" : "en-IN";
}

const subscribe = (l: () => void) => {
  listeners.add(l);
  return () => listeners.delete(l);
};

/** For a component that shows a time and isn't re-created when the setting changes (the top bar, say) */
export function useTimeFormat(): TimeFormat {
  return useSyncExternalStore(subscribe, getTimeFormat, getTimeFormat);
}

/** Changes whenever the clock style or the date order does: for the frame around the screens and anything that must redraw on either */
export function useDisplayKey(): string {
  return useSyncExternalStore(subscribe, () => `${current}-${currentDate}`, () => `${current}-${currentDate}`);
}

/**
 * Locale options that follow the chosen format. The hour cycle is decided here, never by the caller, and it is set with
 * `hourCycle` rather than `hour12: false` because that shows midnight as "24:05" in some browsers. A 2-digit hour
 * becomes a plain one in 12-hour time so it reads "2:30 pm", not "02:30 pm".
 */
export function withClock(o: Intl.DateTimeFormatOptions = {}, format: TimeFormat = current): Intl.DateTimeFormatOptions {
  const twelve = format === "12h";
  const next: Intl.DateTimeFormatOptions = { ...o, hourCycle: twelve ? "h12" : "h23" };
  delete next.hour12;
  if (o.hour) next.hour = twelve ? "numeric" : "2-digit";
  return next;
}

/** "2:30 pm" or "14:30" (add `seconds` for timelines) */
export function formatTime(d: Date | string | number, o: { seconds?: boolean; timeZone?: string } = {}): string {
  return new Date(d).toLocaleTimeString(dateLocale(), withClock({ hour: "2-digit", minute: "2-digit", ...(o.seconds ? { second: "2-digit" } : {}), ...(o.timeZone ? { timeZone: o.timeZone } : {}) }));
}

/** "21 Oct, 2:30 pm" / "21 Oct, 14:30" */
export function formatDateTime(d: Date | string | number, o: Intl.DateTimeFormatOptions = { day: "numeric", month: "short" }): string {
  return new Date(d).toLocaleString(dateLocale(), withClock({ ...o, hour: "2-digit", minute: "2-digit" }));
}
